import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Button } from 'primereact/button';

import Loading from "../../../components/Loading";
import Menu from "../../../components/Menu";

import api from "../../../services/api";
import { Toast } from "primereact/toast";

import "./styles.css";

const filterStorageKeys = {
    quebrasRolo: "8F3K1P-quebras-rolo-filtro",
    efficiency: "8F3K1P-eficiencia-filtro",
    quebrasTrama: "8F3K1P-quebras-trama-filtro",
    quebrasUrdume: "8F3K1P-quebras-urdume-filtro"
};

const normalizeTearCode = (tearCode) => String(Number(tearCode));

const normalizeArticleCode = (articleCode) => {
    const normalizedCode = String(articleCode ?? "").trim();

    return /^\d+$/.test(normalizedCode)
        ? String(Number(normalizedCode))
        : normalizedCode;
};

const decimalFormatter = new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

const formatDecimalValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return "—";
    }

    const numericValue = Number(value);

    return Number.isFinite(numericValue) ? decimalFormatter.format(numericValue) : "—";
};

const formatDateTime = (timestamp) => {
    if (!timestamp) return "--/--/---- --:--";
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    
    return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const getStoredFilterValues = () => {
    if (typeof localStorage === "undefined") {
        return Object.keys(filterStorageKeys).reduce((values, filter) => {
            values[filter] = "";
            return values;
        }, {});
    }

    return Object.entries(filterStorageKeys).reduce((storedValues, [filter, storageKey]) => {
        storedValues[filter] = localStorage.getItem(storageKey) || "";
        return storedValues;
    }, {});
};

const getStoredActiveFilters = () => {
    if (typeof localStorage === "undefined") {
        return Object.keys(filterStorageKeys).reduce((filters, filter) => {
            filters[filter] = false;
            return filters;
        }, {});
    }

    return Object.entries(filterStorageKeys).reduce((storedFilters, [filter, storageKey]) => {
        storedFilters[filter] = localStorage.getItem(`${storageKey}-ativo`) === "true";
        return storedFilters;
    }, {});
};

export default function Layout() {
    const [artigos, setArtigos] = useState([]);
    const [artigosPcp, setArtigosPcp] = useState([]);
    const [tearesRolo, setTearesRolo] = useState([]);
    const [search, setSearch] = useState("");
    const [filterValues, setFilterValues] = useState(getStoredFilterValues);
    const [activeFilters, setActiveFilters] = useState(getStoredActiveFilters);
    const [values, setValues] = useState([]);
    const [matrix, setMatrix] = useState([]);
    const [isComparisonExpanded, setIsComparisonExpanded] = useState(false);

    const [isLoading, setIsLoading] = useState(false);

    const toast = useRef();

    const updateFilterValue = (filter, value) => {
        setFilterValues((currentValues) => ({
            ...currentValues,
            [filter]: value
        }));

        setActiveFilters((currentFilters) => ({
            ...currentFilters,
            [filter]: value !== ""
        }));
    };

    const toggleFilter = (filter) => {
        setActiveFilters((currentFilters) => ({
            ...currentFilters,
            [filter]: !currentFilters[filter]
        }));
    };

    const handleFilterKeyDown = (event, filter) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleFilter(filter);
        }
    };

    const filterByQuebrasRolo = useCallback((teares, filterValue) => {
        if (filterValue === "") {
            return teares;
        }

        const quebrasLimit = Number(filterValue);

        if (!Number.isFinite(quebrasLimit)) {
            return teares;
        }

        return teares.filter((tear) => Number(tear.quebrasRolo) > quebrasLimit);
    }, []);
    const filterByQuebrasTrama = useCallback((teares, filterValue) => {
        if (filterValue === "") {
            return teares;
        }

        const quebrasLimit = Number(filterValue);

        if (!Number.isFinite(quebrasLimit)) {
            return teares;
        }

        return teares.filter((tear) => Number(tear.quebra_trama_atual) > quebrasLimit);
    }, []);

    const filterByQuebrasUrdume = useCallback((teares, filterValue) => {
        if (filterValue === "") {
            return teares;
        }

        const quebrasLimit = Number(filterValue);

        if (!Number.isFinite(quebrasLimit)) {
            return teares;
        }

        return teares.filter((tear) => Number(tear.quebra_urdume_atual) > quebrasLimit);
    }, []);

    const filteredValues = useMemo(() => {
        let teares = values;

        if (activeFilters.quebrasRolo) {
            teares = filterByQuebrasRolo(teares, filterValues.quebrasRolo);
        }

        if (activeFilters.quebrasTrama) {
            teares = filterByQuebrasTrama(teares, filterValues.quebrasTrama);
        }

        if (activeFilters.quebrasUrdume) {
            teares = filterByQuebrasUrdume(teares, filterValues.quebrasUrdume);
        }

        if (activeFilters.efficiency && filterValues.efficiency !== "") {
            const efficiencyLimit = Number(filterValues.efficiency);

            if (Number.isFinite(efficiencyLimit)) {
                teares = teares.filter((tear) => {
                    return Number(tear.eficiencia) < efficiencyLimit;
                });
            }
        }

        return teares;
    }, [
        activeFilters,
        filterValues,
        filterByQuebrasRolo,
        filterByQuebrasTrama,
        filterByQuebrasUrdume,
        values
    ]);

    useEffect(() => {
        if (typeof localStorage === "undefined") {
            return;
        }

        Object.entries(filterStorageKeys).forEach(([filter, storageKey]) => {
            localStorage.setItem(storageKey, filterValues[filter]);
            localStorage.setItem(`${storageKey}-ativo`, String(activeFilters[filter]));
        });
    }, [activeFilters, filterValues]);

    const showError = useCallback((error) => {
        toast.current?.show({
            summary: "Ocorreu um Erro",
            severity: "error",
            detail: error.response ? error.response.data.message : error.message
        });
    }, []);

    const fetchData = useCallback(() => {
        setIsLoading(true);
        Promise.all([
            api.get("layout"),
            api.get("tear-rolo-urdume")
        ]).then(([layoutResponse, quebrasRoloResponse]) => {
            setIsLoading(false);

            const quebrasRoloPorTear = quebrasRoloResponse.data.reduce((accumulator, item) => {
                const tearCode = normalizeTearCode(item.cod_tear);
                accumulator[tearCode] = (accumulator[tearCode] || 0) + (Number(item.quebras) || 0);

                return accumulator;
            }, {});

            const data = layoutResponse.data.map((item) => ({
                ...item,
                quebrasRolo: quebrasRoloPorTear[normalizeTearCode(item.tear)] || 0
            }));

            const artigos = [];

            data.forEach((item) => {
                if (!artigos.includes(item.artigo)) {
                    artigos.push(item.artigo);
                }
            });


            const artigosData = [];

            artigos.forEach((artigo) => {
                const teares = data.filter((element) => {
                    return element.artigo === artigo;
                });

                let toyota = 0;
                let picanol = 0;

                teares.forEach((item) => {
                    if (parseInt(item.tear, 10) <= 60) {
                        picanol += 1;
                    } else {
                        toyota += 1;
                    }
                });

                artigosData.push({
                    "artigo": artigo,
                    "picanol": picanol,
                    "toyota": toyota
                });
            });


            artigosData.sort((a, b) => b.picanol - a.picanol);

            setArtigos(artigosData);
            setTearesRolo(quebrasRoloResponse.data);
            setValues(data);
        }).catch((error) => {
            setIsLoading(false);
            showError(error);
        });
    }, [showError]);

    const metrosPorArtigo = useMemo(() => {
        const artigosData = values.reduce((accumulator, item) => {
            const artigo = String(item.artigo);

            if (!accumulator[artigo]) {
                accumulator[artigo] = {
                    artigo,
                    metros: 0,
                    quantidade_rolos: 0
                };
            }

            accumulator[artigo].metros += Number(item.metros_tecido) || 0;
            accumulator[artigo].quantidade_rolos += 1;

            return accumulator;
        }, {});

        return Object.values(artigosData).map((item) => {
            return {
                ...item,
                metros: Math.round(item.metros)
            };
        }).sort((a, b) => a.artigo.localeCompare(b.artigo));
    }, [values]);

    const fetchArtigosPcp = useCallback(() => {
        return api.get("cadastros/artigos-pcp").then(response => {
            setArtigosPcp(response.data.dados || []);
        }).catch(error => {
            showError(error);
        });
    }, [showError]);

    const artigosPcpPorFamilia = useMemo(() => {
        const tearesPorArtigo = values.reduce((accumulator, item) => {
            const artigo = String(item.artigo);
            accumulator[artigo] = (accumulator[artigo] || 0) + 1;

            return accumulator;
        }, {});

        const familias = artigosPcp.reduce((accumulator, item) => {
            const familia = item.familia || "Sem família";
            const artigo = String(item.artigo_id);

            if (!accumulator[familia]) {
                accumulator[familia] = {};
            }

            accumulator[familia][artigo] = {
                artigo,
                teares: tearesPorArtigo[artigo] || 0
            };

            return accumulator;
        }, {});

        return Object.entries(familias).map(([familia, artigos]) => {
            return {
                familia,
                artigos: Object.values(artigos)
                    .filter((artigo) => artigo.teares > 0)
                    .sort((a, b) => a.artigo.localeCompare(b.artigo))
            };
        }).filter((grupo) => grupo.artigos.length > 0)
            .sort((a, b) => a.familia.localeCompare(b.familia));
    }, [artigosPcp, values]);

    const comparacaoArtigosPorTear = useMemo(() => {
        const comparacaoPorTear = {};

        values.forEach((item) => {
            const tearCode = normalizeTearCode(item.tear);

            if (!comparacaoPorTear[tearCode]) {
                comparacaoPorTear[tearCode] = {
                    tear: tearCode,
                    artigosLayout: new Set(),
                    artigosRolo: new Set(),
                    quebrasRolo: 0,
                    quebraTramaAtual: null,
                    quebraUrdumeAtual: null,
                    eficiencia: null,
                    tramaCmpx: null,
                    urdumeCmpx: null
                };
            }

            comparacaoPorTear[tearCode].artigosLayout.add(normalizeArticleCode(item.artigo));
            comparacaoPorTear[tearCode].quebraTramaAtual = item.quebra_trama_atual;
            comparacaoPorTear[tearCode].quebraUrdumeAtual = item.quebra_urdume_atual;
            comparacaoPorTear[tearCode].eficiencia = item.eficiencia;
            comparacaoPorTear[tearCode].tramaCmpx = item.trama_cmpx;
            comparacaoPorTear[tearCode].urdumeCmpx = item.urdume_cmpx;
        });

        tearesRolo.forEach((item) => {
            const tearCode = normalizeTearCode(item.cod_tear);

            if (!comparacaoPorTear[tearCode]) {
                comparacaoPorTear[tearCode] = {
                    tear: tearCode,
                    artigosLayout: new Set(),
                    artigosRolo: new Set(),
                    quebrasRolo: 0,
                    quebraTramaAtual: null,
                    quebraUrdumeAtual: null,
                    eficiencia: null,
                    tramaCmpx: null,
                    urdumeCmpx: null
                };
            }

            comparacaoPorTear[tearCode].artigosRolo.add(normalizeArticleCode(item.cod_artigo));
            comparacaoPorTear[tearCode].quebrasRolo += Number(item.quebras) || 0;
        });

        return Object.values(comparacaoPorTear).map((item) => {
            const artigosLayout = [...item.artigosLayout].filter(Boolean).sort();
            const artigosRolo = [...item.artigosRolo].filter(Boolean).sort();
            const artigosIguais = artigosLayout.length === artigosRolo.length
                && artigosLayout.every((artigo, index) => artigo === artigosRolo[index]);

            let status = "Artigos iguais";

            if (artigosLayout.length === 0) {
                status = "Ausente no layout";
            } else if (artigosRolo.length === 0) {
                status = "Ausente em tear-rolo-urdume";
            } else if (!artigosIguais) {
                status = "Artigos divergentes";
            }

            return {
                tear: item.tear,
                artigoLayout: artigosLayout.join(", ") || "—",
                artigoRolo: artigosRolo.join(", ") || "—",
                quebrasRolo: artigosRolo.length ? item.quebrasRolo : "—",
                quebraTramaAtual: item.quebraTramaAtual ?? "—",
                tramaCmpx: formatDecimalValue(item.tramaCmpx),
                quebraUrdumeAtual: item.quebraUrdumeAtual ?? "—",
                urdumeCmpx: formatDecimalValue(item.urdumeCmpx),
                eficiencia: formatDecimalValue(item.eficiencia),
                status,
                hasDifference: status !== "Artigos iguais"
            };
        }).sort((a, b) => Number(a.tear) - Number(b.tear));
    }, [tearesRolo, values]);

    const getComparisonRowClassName = (rowData) => {
        return rowData.hasDifference ? "layout-comparison-row-difference" : "";
    };

    const getFamiliaLabel = (familia) => {
        return familia === "Sem família" ? familia : `Família ${familia}`;
    };

    const ultimasAtualizacoes = useMemo(() => {
    let picanolLatest = null;
    let toyotaLatest = null;

    values.forEach((item) => {
        if (!item.updated_at || !item.modelo) return;

        const date = new Date(item.updated_at).getTime();
        const modelo = String(item.modelo).toUpperCase();

        if (modelo.includes("PICANOL")) {
            if (!picanolLatest || date > picanolLatest) picanolLatest = date;
        } else if (modelo.includes("TOYOTA")) {
            if (!toyotaLatest || date > toyotaLatest) toyotaLatest = date;
        }
    });

    return {
        picanol: formatDateTime(picanolLatest),
        toyota: formatDateTime(toyotaLatest)
    };
}, [values]);

    useEffect(() => {
        fetchData();
        fetchArtigosPcp();

        const matrixData = [];

        for (let i = 1; i <= 16; i++) {
            const array = [];
            for (let j = 1; j <= 8; j++) {
                array.push(j);
            }
            matrixData.push(array);
        }
        setMatrix(matrixData);
    }, [fetchData, fetchArtigosPcp]);

    return (
        <div>
            <Menu />
            <Toast ref={toast} />

            {isLoading ? (
                <Loading />
            ) : (
                <Card
                    className="p-m-1 layout-card"
                    title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Layout da Sala</span>
                                <span style={{ fontSize: '1rem', color: '#6c757d', fontWeight: 'normal' }}>
                                    Toyota: {ultimasAtualizacoes.toyota} | Picanol: {ultimasAtualizacoes.picanol}
                                </span>
                            </div>
                            <Button 
                                className="p-button p-button-sm" 
                                label="Recarregar" 
                                icon="pi pi-refresh" 
                                onClick={fetchData} 
                            />
                        </div>
                    }
                >
                    <div className="layout-filters">
                        <div
                            aria-pressed={activeFilters.quebrasRolo}
                            className={`layout-filter layout-filter-quebras-rolo ${activeFilters.quebrasRolo ? "is-active" : ""}`}
                            onClick={() => toggleFilter("quebrasRolo")}
                            onKeyDown={(event) => handleFilterKeyDown(event, "quebrasRolo")}
                            role="button"
                            tabIndex={0}
                        >
                            <span>Nº Quebras de Rolo</span>
                            <input
                                aria-label="Número de quebras de rolo"
                                onChange={(event) => updateFilterValue("quebrasRolo", event.target.value)}
                                onClick={(event) => event.stopPropagation()}
                                type="number"
                                value={filterValues.quebrasRolo}
                            />
                        </div>

                        <div
                            aria-pressed={activeFilters.efficiency}
                            className={`layout-filter layout-filter-efficiency ${activeFilters.efficiency ? "is-active" : ""}`}
                            onClick={() => toggleFilter("efficiency")}
                            onKeyDown={(event) => handleFilterKeyDown(event, "efficiency")}
                            role="button"
                            tabIndex={0}
                        >
                            <span>Eficiência abaixo de</span>
                            <input
                                aria-label="Eficiência abaixo de"
                                onChange={(event) => updateFilterValue("efficiency", event.target.value)}
                                onClick={(event) => event.stopPropagation()}
                                type="number"
                                value={filterValues.efficiency}
                            />
                        </div>

                        <div
                            aria-pressed={activeFilters.quebrasTrama}
                            className={`layout-filter layout-filter-quebras-trama ${activeFilters.quebrasTrama ? "is-active" : ""}`}
                            onClick={() => toggleFilter("quebrasTrama")}
                            onKeyDown={(event) => handleFilterKeyDown(event, "quebrasTrama")}
                            role="button"
                            tabIndex={0}
                        >
                            <span>Quebras de Trama acima de</span>
                            <input
                                aria-label="Quebras de trama acima de"
                                onChange={(event) => updateFilterValue("quebrasTrama", event.target.value)}
                                onClick={(event) => event.stopPropagation()}
                                type="number"
                                value={filterValues.quebrasTrama}
                            />
                        </div>

                        <div
                            aria-pressed={activeFilters.quebrasUrdume}
                            className={`layout-filter layout-filter-quebras-urdume ${activeFilters.quebrasUrdume ? "is-active" : ""}`}
                            onClick={() => toggleFilter("quebrasUrdume")}
                            onKeyDown={(event) => handleFilterKeyDown(event, "quebrasUrdume")}
                            role="button"
                            tabIndex={0}
                        >
                            <span>Quebras de Urdume acima de</span>
                            <input
                                aria-label="Quebras de urdume acima de"
                                onChange={(event) => updateFilterValue("quebrasUrdume", event.target.value)}
                                onClick={(event) => event.stopPropagation()}
                                type="number"
                                value={filterValues.quebrasUrdume}
                            />
                        </div>
                    </div>

                    <div className="layout-toolbar">
                        <InputText
                            className="layout-search"
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Pesquisar ARTIGO"
                            value={search}
                        />
                    </div>

                    <div className="p-grid">
                        <div className="p-col-10">
                            <div className="layout-room">
                            {matrix.map((row, i) => {
                                return (
                                    <div key={`row-${i}`} className="layout-room-column">
                                        {row.map((col) => {
                                            const tear = filteredValues.find((element) =>
                                                parseInt(element.pos_x, 10) === i + 1 && parseInt(element.pos_y, 10) === col
                                            );
                                            const isSelected = tear && search === tear.artigo;

                                            const withFilter =
                                                (activeFilters.quebrasTrama && filterValues.quebrasTrama !== "") ||
                                                (activeFilters.quebrasUrdume && filterValues.quebrasUrdume !== "");

                                            const variant = withFilter ? "filter" : activeFilters.efficiency ? "efficiency" : "";
                                            const cellSuffix = variant ? `-${variant}` : "";
                                            const roomBreaksModifier = variant ? `is-${variant}` : ""; 

                                            return (
                                                <div className={`layout-room-cell${cellSuffix}`} key={`${i + 1}-${col}`}>
                                                    {tear && (
                                                        <div className={`layout-loom ${isSelected ? "is-selected" : ""}`}>
                                                            <h3 className="layout-loom-label">TEAR</h3>
                                                            <h2 className="layout-loom-number">{tear.tear}</h2>
                                                            <h2 className={`layout-loom-article ${isSelected ? "is-selected" : ""}`}>
                                                                {tear.artigo}
                                                            </h2>
                                                            <span
                                                                aria-label={`${tear.quebrasRolo} quebras no rolo`}
                                                                className={`layout-loom-roll-breaks ${roomBreaksModifier}`}
                                                                title="Quebras no rolo"
                                                            >
                                                                {tear.quebrasRolo}
                                                            </span>
                                                            <div className="layout-loom-active-filters">
                                                                {activeFilters.quebrasTrama && filterValues.quebrasTrama !== "" && (
                                                                    <span className="layout-loom-badge badge-trama" title="Quebras de Trama Atual">
                                                                        T: {tear.quebra_trama_atual ?? "—"}
                                                                    </span>
                                                                )}
                                                                {activeFilters.quebrasUrdume && filterValues.quebrasUrdume !== "" && (
                                                                    <span className="layout-loom-badge badge-urdume" title="Quebras de Urdume Atual">
                                                                        U: {tear.quebra_urdume_atual ?? "—"}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="layout-loom-efficiency-filter">
                                                                {activeFilters.efficiency && filterValues.efficiency !== "" && (
                                                                    <span className="layout-loom-badge badge-efficiency" title="Eficiência Atual">
                                                                        {tear.eficiencia ? `${formatDecimalValue(tear.eficiencia)}%` : "—"}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )
                            })}
                            </div>
                        </div>
                        <div className="p-col-2">
                            <div className="layout-table-group layout-summary-table">
                                <div className="layout-table-header">
                                    <h4 className="layout-table-title">Resumo por artigo</h4>
                                </div>

                                <DataTable
                                    emptyMessage="Nenhum registro encontrado."
                                    responsiveLayout="scroll"
                                    size="small"
                                    value={artigos}
                                >
                                    <Column header="Artigo" field="artigo" />
                                    <Column header="Picanol" field="picanol" />
                                    <Column header="Toyota" field="toyota" />
                                </DataTable>
                            </div>
                        </div>
                    </div>

                    <div className="layout-production-section">
                        <h3 className="layout-section-title">
                            Metros por artigo
                        </h3>

                        <div className="layout-table-group layout-production-table">
                            <div className="layout-table-header">
                                <h4 className="layout-table-title">Produção por artigo</h4>
                            </div>

                            <DataTable
                                emptyMessage="Nenhum registro encontrado."
                                responsiveLayout="scroll"
                                size="small"
                                value={metrosPorArtigo}
                            >
                                <Column header="Artigo" field="artigo" />
                                <Column header="Metros" field="metros" />
                                <Column header="Quantidade de Rolos" field="quantidade_rolos" />
                            </DataTable>
                        </div>
                    </div>

                    <div className="layout-family-section">
                        <h3 className="layout-section-title">
                            Teares por família
                        </h3>

                        <div className="layout-family-tables">
                            {artigosPcpPorFamilia.map((grupo) => {
                                return (
                                    <div
                                        className="layout-table-group"
                                        key={grupo.familia}
                                    >
                                        <div className="layout-table-header">
                                            <h4 className="layout-table-title">
                                                {getFamiliaLabel(grupo.familia)}
                                            </h4>
                                        </div>

                                        <DataTable
                                            emptyMessage="Nenhum registro encontrado."
                                            responsiveLayout="scroll"
                                            size="small"
                                            value={grupo.artigos}
                                        >
                                            <Column header="Artigo" field="artigo" />
                                            <Column header="Teares" field="teares" />
                                        </DataTable>
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    <div className="layout-comparison-section">
                        <button
                            aria-expanded={isComparisonExpanded}
                            className="layout-comparison-toggle"
                            onClick={() => setIsComparisonExpanded((isExpanded) => !isExpanded)}
                            type="button"
                        >
                            <span>Comparação de artigos por tear</span>
                            <span aria-hidden="true">
                                {isComparisonExpanded ? "−" : "+"}
                            </span>
                        </button>

                        {isComparisonExpanded ? (
                            <div className="layout-table-group layout-comparison-table">
                                <DataTable
                                    emptyMessage="Nenhum registro encontrado."
                                    responsiveLayout="scroll"
                                    rowClassName={getComparisonRowClassName}
                                    size="small"
                                    value={comparacaoArtigosPorTear}
                                >
                                    <Column header="Tear" field="tear" />
                                    <Column header="Artigo — layout" field="artigoLayout" />
                                    <Column header="Artigo — tear-rolo-urdume" field="artigoRolo" />
                                    <Column header="Quebras de rolo" field="quebrasRolo" />
                                    <Column header="Quebras de trama" field="quebraTramaAtual" />
                                    <Column header="Trama CMPX" field="tramaCmpx" />
                                    <Column header="Quebras de urdume" field="quebraUrdumeAtual" />
                                    <Column header="Urdume CMPX" field="urdumeCmpx" />
                                    <Column header="Eficiência" field="eficiencia" />
                                    <Column header="Situação" field="status" />
                                </DataTable>
                            </div>
                        ) : null}
                    </div>
                </Card>
            )}
        </div>
    )
}
